import React from 'react';
import { Container, Typography, Box } from '@mui/material';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const stats = [
  {
    number: 10,
    suffix: '+',
    title: 'Industries Served',
    description: 'From Healthcare to FinTech, delivering innovative solutions across diverse sectors'
  },
  {
    number: 8,
    suffix: '+',
    title: 'Young & Ambitious Team',
    description: 'Driven by fresh ideas, rapid learning, and a passion for building impactful technology.'
  },
  {
    number: 90,
    suffix: '%',
    title: 'Faster Time-to-Market',
    description: 'Accelerated development cycles with our agile methodology and expert team'
  },
  {
    number: 400,
    suffix: '+',
    title: 'Development Hours Saved',
    description: 'Efficient processes and automation saving valuable development time for our clients'
  }
];

const StatsSection = () => {
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true
  });

  return (
    <Box
      ref={ref}
      sx={{
        py: { xs: 10, md: 15 },
        background: '#f8f9fa',
        position: 'relative'
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: { xs: 8, md: 10 } }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2rem', sm: '2.5rem', md: '2.5rem' },
                fontWeight: 800,
                color: '#111',
                mb: { xs: 0.5, md: 3 }
              }}
            >
              Driving Digital Success with
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2rem', sm: '2.5rem', md: '2.5rem' },
                fontWeight: 800,
                color: '#111',
                mb: 4
              }}
            >
              Speed, Scalability, and Strategy
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: '1.1rem', md: '1.25rem' },
                color: '#555',
                maxWidth: '800px',
                margin: '0 auto',
                lineHeight: 1.8
              }}
            >
              At Virves, we turn challenges into growth with tech-driven solutions. Our experienced team crafts scalable, high-performance solutions, empowering businesses to innovate faster, streamline development, and create lasting competitive advantage in ever-evolving markets.
            </Typography>
          </motion.div>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' },
            gap: { xs: 6, md: 4 },
            mt: 8
          }}
        >
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <Box
                sx={{
                  textAlign: 'center',
                  position: 'relative'
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: '3.5rem', md: '4rem' },
                    fontWeight: 700,
                    color: '#3F72AF',
                    lineHeight: 1,
                    mb: 1
                  }}
                >
                  {stat.number}{stat.suffix}
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: '1.25rem', md: '1.35rem' },
                    fontWeight: 600,
                    color: '#111',
                    mb: 2,
                    position: 'relative',
                    paddingBottom: '1rem',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '40px',
                      height: '2px',
                      backgroundColor: '#3F72AF',
                      opacity: 0.7
                    }
                  }}
                >
                  {stat.title}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.95rem',
                    color: '#666',
                    lineHeight: 1.6
                  }}
                >
                  {stat.description}
                </Typography>
              </Box>
            </motion.div>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default StatsSection; 